import type { ImageType } from '@vizit/api';
import { Badge, BadgeLevel, BadgeShape, BadgeSize, Button, ButtonLevel, ButtonSize } from '@vizit/atoms';
import { RadialProgress, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { AlertCircleIcon, PlusIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import { plural } from '@vizit/utils';
import * as React from 'react';
import { ImageTypeChip } from '../image-type-chip';
import { ScoringMeasureGroup, ScoringMeasureLink, scoredAgainstLabel, showingLabel } from '../scoring-measure-group';
import { ScoringStatus, type ScoringStatusProps } from '../scoring-status';

export interface BenchmarkSummaryBarProps extends ScoringStatusProps {
  /**
   * Number of images in the benchmark, read as "Showing N images"
   */
  imageCount: number;
  /**
   * Images needed before the benchmark can be scored under a category; under it the bar says how many are missing
   * and "Add a product category" is disabled
   */
  minimumImages: number;
  /**
   * Whether the grid shows scores under the selected category, i.e. its scoring is ready. Only then does the bar
   * read "Showing N images scored against"
   *
   * @optional
   * @default false
   */
  isScored?: boolean;
  /**
   * The image type the benchmark grades, locked at creation
   */
  scope: ImageType;
  /**
   * The product category control, e.g. the category picker. Leave unset while the benchmark has no category: the
   * bar then offers "Add a product category" instead
   *
   * @optional
   */
  productCategoryControl?: React.ReactNode;
  /**
   * Called from "Add a product category". The button renders without it but does nothing.
   *
   * @optional
   */
  onAddProductCategory?: () => void;
  /**
   * Number of images still processing; shows an inline status while above zero
   *
   * @optional
   * @default 0
   */
  processingCount?: number;
  /**
   * Controls on the right, e.g. "Upload images"
   *
   * @optional
   */
  actions?: React.ReactNode;
  /**
   * Additional CSS classes for the root element
   *
   * @optional
   */
  className?: string;
}

/**
 * The bar over a benchmark's grid that says what its scores are measured against: "Showing N images" ("… scored
 * against" once the selected category's scoring is ready), then the product category control (or "Add a product
 * category" while there is none) and the locked image type in one grey group. While the set is under the scoring
 * minimum a warning says how many images are missing, and adding a category waits for them. The scoring and
 * processing statuses follow; `actions` fills the right.
 */
export function BenchmarkSummaryBar(props: BenchmarkSummaryBarProps) {
  const {
    imageCount,
    minimumImages,
    isScored = false,
    scope,
    productCategoryControl,
    onAddProductCategory,
    processingCount = 0,
    scoringCategoryName,
    scoringProgress,
    failedScoring,
    actions,
    className,
  } = props;

  const imagesNeeded = Math.max(minimumImages - imageCount, 0);
  const imagesNeededId = React.useId();

  return (
    <div className={cx('flex flex-wrap items-center justify-between gap-6', className)}>
      <div className="flex flex-wrap items-center gap-4">
        <Text size={TextSize.Md} weight={TextWeight.Medium} color={TextColor.Tertiary}>
          {isScored ? scoredAgainstLabel(imageCount, 'image') : showingLabel(imageCount, 'image')}
        </Text>
        <ScoringMeasureGroup>
          {productCategoryControl ?? (
            <Button
              level={ButtonLevel.Secondary}
              size={ButtonSize.Md}
              iconStart={<PlusIcon size={14} aria-hidden />}
              onPress={onAddProductCategory}
              // The API refuses a scoring under the minimum; the warning beside the group says what is missing.
              isDisabled={imagesNeeded > 0}
              aria-describedby={imagesNeeded > 0 ? imagesNeededId : undefined}
            >
              Add a product category
            </Button>
          )}
          <ScoringMeasureLink />
          <ImageTypeChip scope={scope} />
        </ScoringMeasureGroup>
        {imagesNeeded > 0 && (
          <Badge
            id={imagesNeededId}
            level={BadgeLevel.Warning}
            shape={BadgeShape.Box}
            size={BadgeSize.Lg}
            className="h-20 gap-2 pr-6 pl-5 font-medium text-md"
          >
            <AlertCircleIcon size={12} className="text-fg-warning-secondary" aria-hidden />
            <span>{plural(imagesNeeded, 'more image')} needed</span>
          </Badge>
        )}
        <ScoringStatus
          scoringCategoryName={scoringCategoryName}
          scoringProgress={scoringProgress}
          failedScoring={failedScoring}
        />
        {processingCount > 0 && (
          <output className="flex items-center gap-2">
            <RadialProgress isIndeterminate size={8} aria-hidden />
            <Text size={TextSize.Xs} color={TextColor.Tertiary}>
              Processing {plural(processingCount, 'image')}…
            </Text>
          </output>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-4">{actions}</div>}
    </div>
  );
}
